import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { MessageList } from '#src/components/message-list';
import { toaster } from '#src/components/ui/toaster';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';
import { GET_MESSAGES } from '#src/graphql/queries/messages';
import {
  usePageFooter,
  usePageLayout,
} from '#src/providers/page-layout-provider';
import { useCurrentUser } from '#src/providers/user-provider';

export function Dashboard() {
  const { footerRef } = usePageFooter();
  const { setFooterMode } = usePageLayout();
  const user = useCurrentUser();

  useEffect(() => {
    setFooterMode('fixed');
    return () => setFooterMode('float');
  }, [setFooterMode]);
  const [messageText, setMessageText] = useState('');
  const [parentTargetId, setParentTargetId] = useState<string | null>(null);

  const [createMessage, { loading: isSendingMessage }] =
    useMutation(CREATE_MESSAGE);

  const {
    data: messagesData,
    loading: isLoadingMessages,
    error: messagesError,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    fetchPolicy: 'cache-and-network',
  });

  const filteredMessages = [...(messagesData?.messages || [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [filteredMessages]);

  const handleSendMessage = async () => {
    const value = messageText.trim();
    if (!value) return;
    try {
      await createMessage({
        variables: {
          input: {
            value,
            parentMessageId: parentTargetId ?? undefined,
          },
        },
      });
      setMessageText('');
      await refetchMessages();
      toaster.create({
        title: 'Message sent',
        description: 'Your message was created successfully',
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: 'Failed to send message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const footerContent = user.email ? (
    <VStack gap={2} align="stretch" width="100%">
      {parentTargetId && (
        <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
          <Text>Replying to message</Text>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => setParentTargetId(null)}
          >
            Cancel
          </Button>
        </HStack>
      )}
      <ChatComposerRow
        value={messageText}
        onChange={setMessageText}
        onSubmit={handleSendMessage}
        placeholder="Type a message..."
        loading={isSendingMessage}
      />
    </VStack>
  ) : null;

  return (
    <>
      {/* Render message input into footer portal */}
      {footerRef.current && createPortal(footerContent, footerRef.current)}

      <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
        <HStack
          flex={1}
          minHeight={0}
          align="stretch"
          gap={4}
          overflow="hidden"
        >
          <Box flex={1} minHeight={0} display="flex" flexDirection="column">
            {!user.email ? (
              <Alert.Root status="info">
                <Alert.Indicator />
                <Alert.Title>
                  Please set your user email in Settings to view messages.
                </Alert.Title>
              </Alert.Root>
            ) : (
              <MessageList
                messages={filteredMessages}
                loading={isLoadingMessages}
                error={messagesError ?? undefined}
                emptyMessage="No messages found."
                parentTargetId={parentTargetId}
                onSetParentTargetId={setParentTargetId}
                messagesEndRef={messagesEndRef}
              />
            )}
          </Box>
        </HStack>
      </Box>
    </>
  );
}
